import { useEffect, useRef, type ReactNode } from 'react';
import { DialogHost } from '../ui/dialogHost';
import { useEscapeLayer } from '../ui/Dialog';
import { inertOthers } from '../ui/focus';

/**
 * The event card beside the calendar (desktop) or over it (phone). On open the heading gets focus.
 * Escape never closes the event card (owner 2026-10-23 21:58: „esc ma nie zamykac karty wydarzenia”) — it still closes
 * the dialogs, menus and pickers over it, clears the quote selection and drops a field's edit (their own layers / handlers).
 * `closeOnEscape` (the new event / rental form in this panel): Escape closes it as before (not from a text field or
 * form; any popup over it closes first — the layers of ui/Dialog.tsx).
 * On a phone, where it covers the screen's content, the rest of that content is inert (the app bars stay usable). Giving focus back to the opener is the page's job.
 * On a desktop the card is its dialogs' host: they open centred on it, in "Pełny widok" on the full view (owner 2026-10-21 23:46).
 */
export function SidePanel({ id, label, focusKey, phone, closeOnEscape = false, onClose, children }: { id?: string; label: string; focusKey: string; phone: boolean; closeOnEscape?: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const heading = ref.current?.querySelector<HTMLElement>('h2');
    if (!heading) return;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: !phone });
  }, [focusKey, phone]);

  // the bottom layer of Escape (ui/Dialog.tsx): a dialog, a "…" menu, the bell or any other popup closes first (review I1).
  // The event card has no layer at all: its Escape is not taken, so nothing under it acts differently.
  useEscapeLayer(
    (e) => {
      // a field uses Escape itself (the quick picker closes its list, then clears), and unsaved edits are not thrown away
      if (e.target instanceof Element && e.target.closest('input, textarea, select, form')) return false;
      // a hint or the calendar's quick preview take their own Escape first (they mark it, so this is not reached)
      close.current();
    },
    closeOnEscape,
    { base: true },
  );

  useEffect(() => {
    if (!phone || !ref.current) return;
    // only the screen's content: the app's top and bottom bars stay visible above the panel and keep working
    return inertOthers(ref.current, ref.current.closest('main') ?? document.body);
  }, [phone]);

  return (
    <DialogHost as="aside" ref={ref} id={id} className="cal-panel" aria-label={label} enabled={!phone}>
      {children}
    </DialogHost>
  );
}
